import React, { useMemo } from 'react';
import { PageControl } from '@croquiscom-pvt/zds';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import clsx from 'clsx';

import { A11y, Swiper, SwiperClass, SwiperSlide } from '@common/carousel/swiper';
import { HorizontalProductCardListContainer } from '@common/product-card';
import { PDPGoodsGroup_goods_carousel_component_list as ProductItemType } from '@domains/pdp-common';

import { PAGE_VIEW_COUNT } from '../services/useRelatedStylistListService';

interface RelatedStylistListItemListProps {
  list: ProductItemType[];
  currentIndex: number;
  handleSlideChange: (swiper: SwiperClass) => void;
  onClick: (item: ProductItemType, index: number) => void;
  onImpression: (item: ProductItemType, index: number) => void;
  onSavedProduct: (checked: boolean, item: ProductItemType, index: number) => void;
}

export const RelatedStylistCarousel: React.FC<React.PropsWithChildren<RelatedStylistListItemListProps>> = ({
  list,
  currentIndex,
  handleSlideChange,
  onClick,
  onImpression,
  onSavedProduct,
}) => {
  const dividedList = useMemo(() => {
    const result: ProductItemType[][] = [];
    for (let i = 0; i < list.length; i += PAGE_VIEW_COUNT) {
      const row: ProductItemType[] = [];
      for (let j = 0; j < PAGE_VIEW_COUNT; j++) {
        if (list[i + j]) {
          row.push(list[i + j]);
        }
      }
      result.push(row);
    }
    return result;
  }, [list]);

  return (
    <div css={carouselWrapperCss}>
      <Swiper
        className={clsx([dividedList.length === 1 && 'is_single_list'])}
        slidesPerView='auto'
        spaceBetween={16}
        slidesOffsetBefore={16}
        slidesOffsetAfter={50}
        modules={[A11y]}
        aria-label='이 상품과 함께 착용한 코디 상품'
        role='region'
        aria-roledescription='carousel'
        threshold={20}
        onSlideChange={handleSlideChange}
      >
        {dividedList.map((items, listIndex) => {
          return (
            <SwiperSlide key={listIndex}>
              <HorizontalProductCardListContainer<ProductItemType>
                css={listContainerCss}
                likeButtonType='thumbnail'
                thumbnailWidth={80}
                items={items}
                onClick={(item, index) => onClick(item, listIndex * PAGE_VIEW_COUNT + index)}
                onImpression={(item, index) => onImpression(item, listIndex * PAGE_VIEW_COUNT + index)}
                onLikeClick={(isLiked, item, index) =>
                  onSavedProduct(isLiked, item, listIndex * PAGE_VIEW_COUNT + index)
                }
              />
            </SwiperSlide>
          );
        })}
        {dividedList.length > 1 && (
          <PageControlWrapper className='PageControlWrapper'>
            <PageControl
              color='black'
              size='small'
              currentIndex={currentIndex}
              totalCount={dividedList.length}
              onClick={() => {}}
            />
          </PageControlWrapper>
        )}
      </Swiper>
    </div>
  );
};

const PageControlWrapper = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
`;

const carouselWrapperCss = css`
  .swiper {
    width: 100%;
    height: 100%;
    margin-bottom: 20px;

    .swiper-slide {
      width: calc(100% - 66px);
      height: auto;
    }
    .swiper-pagination-bullets {
      .swiper-pagination-bullets {
        bottom: 0px;
        height: 22px;
      }
    }
    .swiper-wrapper {
      box-sizing: border-box;
    }
  }

  .swiper:not(.is_single_list) {
    padding-bottom: 22px; // bullets 높이 만큼 지정
  }

  .swiper.is_single_list .swiper-slide {
    width: calc(100% - 32px);
  }
`;

const listContainerCss = css`
  margin: 0;
`;
